import { cloneDeep } from 'lodash';
import { API } from '../common/api';
import { BASE_URL, CONTRACT_URL, ESTIMATE_URL, PDF_URL } from '../common/env';
import { CalculatorResult, IEstimate, IEstimateData, Practice, PracticeTypes } from './interfaces';
import { QueueService } from './queue.service';
import i18next from 'i18next';
import AgoToast from '../../components/Toast/AgoToast';
import { IProducer } from '../common/interfaces';
import { Journey } from '../../pages/customer-profile/types/GrowerService';
import { getInitialBasedOnPracticeType } from '../../pages/estimation-tool/common/utils';

const getEstimateById = async (estimateId: string) => {
  return await API.get(`${ESTIMATE_URL}/estimate-service/estimates/${estimateId}`).then(async (res) => {
    if (res.data.data.length === 0) {
      return {
        currentEstimate: res.data,
      };
    }
    const { calculatorResultCombined, calculatorResultA, calculatorResultB } = res.data.result ?? {};
    res.data.data.scenarios = mergeTillagePracticesInScenarios(calculatorResultCombined?.updatedScenarios ?? []);
    return {
      paymentInfo: calculatorResultCombined,
      currentEstimate: res.data,
      optionAPaymentInfo: calculatorResultA,
      optionBPaymentInfo: calculatorResultB,
    };
  });
};

const getDraft = async (forceCreation = false) => {
  const newEstimate = async () => {
    const result = await API.put(`${ESTIMATE_URL}/estimate-service/estimates?ignoreSync=true`, {
      data: [],
      type: 'REGULAR',
      growerId: null,
      journeyId: null,
      version: 'agoro-v1',
    });

    return result.data;
  };

  return API.get(`${ESTIMATE_URL}/estimate-service/estimates/draft`)
    .then(async (res) => {
      if (!res.data.contractVersionId) {
        return newEstimate();
      }
      return res.data;
    })
    .catch(async () => {
      if (forceCreation) {
        return newEstimate();
      }
      return null;
    });
};

const getStandaloneEstimate = async () => {
  return getDraft(false).then(async (res) => {
    if (res.data.length === 0) {
      return {
        currentEstimate: res,
      };
    }
    const { calculatorResultCombined, calculatorResultA, calculatorResultB } = res.result ?? {};
    res.data.scenarios = mergeTillagePracticesInScenarios(calculatorResultCombined?.updatedScenarios ?? []);
    return {
      paymentInfo: calculatorResultCombined,
      optionAPaymentInfo: calculatorResultA,
      optionBPaymentInfo: calculatorResultB,
      currentEstimate: res,
    };
  });
};

const mergeTillagePracticesInScenarios = (scenarios: any[]) => {
  const mergedScenarios = cloneDeep(scenarios);
  return mergedScenarios.map((scenario: any) => {
    if (scenario.practiceType === 'PASTURE') {
      scenario.practiceType = 'PASTURELAND';
    }
    scenario.practices = transformPractices(scenario.practices, scenario.practiceType);
    return scenario;
  });
};

const getIsLegumeBoolean = (isLegume: any) => {
  return isLegume !== false;
};

const transformCoverAnnual = (practice: any, readableCode: string, practiceType?: PracticeTypes) => {
  practice.details = {
    eqCarbon:
      practice?.details?.eqCarbon ?? !practiceType
        ? 0
        : getInitialBasedOnPracticeType(practiceType, 'Cover annual', !practiceType || practiceType === 'ROW_CROP' ? undefined : 'degraded'),
  };
  practice.readableCode = readableCode;
  return practice;
};

const transformCoverCropping = (practice: any, readableCode: string) => {
  practice.details = {
    isLegume: getIsLegumeBoolean(practice.details?.isLegume),
  };
  practice.readableCode = readableCode;
  return practice;
};

const transformNitrogenReduction = (practice: any) => {
  practice.details = {
    yearsEnrolled: practice?.details?.yearsEnrolled || 5,
  };
  practice.readableCode = 'Nitrogen reduction';
  return practice;
};

const transformReduceTillage = (practice: any) => {
  practice.details = {
    new: practice?.details?.new || 'no',
    current: practice?.details?.current || 'intensive',
  };
  practice.readableCode = 'Reduced Tillage';
  practice.name = 'Reduced Tillage';
  practice.shortCode = practice?.details?.new === 'no' ? 'NT' : 'IT';
  practice.id = practice?.details?.new === 'no' ? '202291f1-1331-4c08-bae7-9752b20f838d' : '10478263-c57f-400c-b0c3-3cd9e5ae5362';
  return practice;
};

const addReadableCodeTo = (practice: any, readableCode: string) => {
  practice.readableCode = readableCode;
  return practice;
};

const transformPractices = (practices: Practice[], newEstimateVersion: boolean, practiceType?: PracticeTypes) => {
  return practices.map((practice: any) => {
    if (practice.shortCode === 'CC') {
      return transformCoverCropping(practice, 'Cover cropping');
    }
    if (practice.shortCode === 'NRR') {
      return transformNitrogenReduction(practice);
    }
    if (practice.shortCode === 'LA') {
      return addReadableCodeTo(practice, 'Legume Addition');
    }
    if (practice.shortCode === 'IT') {
      return !newEstimateVersion ? transformReduceTillage(practice) : practice;
    }
    if (practice.shortCode === 'NT') {
      return !newEstimateVersion ? transformReduceTillage(practice) : practice;
    }
    if (practice.shortCode === 'RPBS') {
      return addReadableCodeTo(practice, 'Biodiversity/Seeding');
    }
    if (practice.shortCode === 'RPF') {
      return addReadableCodeTo(practice, 'Fertilization');
    }
    if (practice.shortCode === 'RPIG') {
      return addReadableCodeTo(practice, 'Improved grazing');
    }
    if (practice.shortCode === 'ICL1' || practice.shortCode === 'ICL2') {
      return addReadableCodeTo(practice, 'ICL');
    }
    if (practice.shortCode === 'NO') {
      return addReadableCodeTo(practice, 'Nitrogen Optimization');
    }
    if (practice.shortCode === 'IN') {
      return addReadableCodeTo(practice, 'Inoculant');
    }
    if (practice.shortCode === 'CP') {
      return transformCoverCropping(practice, 'Cover perennial');
    }
    if (practice.shortCode === 'CA') {
      return transformCoverAnnual(practice, 'Cover annual', practiceType);
    }
    return practice;
  });
};

const getAvailablePractices = async (version: string) => {
  const newEstimateVersion = version.startsWith('agoro-');
  return await API.get(`${ESTIMATE_URL}/estimate-service/practices`).then((res) => {
    const displayablePractices = newEstimateVersion ? res.data : res.data.filter((practice: any) => practice.shortCode !== 'NT');

    const ordering = {};
    const sortOrder = newEstimateVersion
      ? ['IT', 'CC', 'CA', 'NT', 'CP', 'NO', 'NRR', 'LA', 'RPBS', 'RPF', 'RPIG', 'IN', 'ICL1', 'ICL2']
      : ['IT', 'NT', 'CC', 'CA', 'CP', 'NO', 'NRR', 'LA', 'RPBS', 'RPF', 'RPIG', 'IN', 'ICL1', 'ICL2'];

    for (let i = 0; i < sortOrder.length; i++) ordering[sortOrder[i]] = i;
    displayablePractices.sort((a, b) => {
      return ordering[a.shortCode] - ordering[b.shortCode] || a.shortCode.localeCompare(b.shortCode);
    });
    return transformPractices(displayablePractices, newEstimateVersion);
  });
};

const syncEstimate = (estimate) => {
  return API.post(`${CONTRACT_URL}/contract-service/contract/sync`, estimate);
};

const saveEstimate = async (estimate: any) => {
  const sync = () => {
    return syncEstimate(estimate);
  };
  const response = await API.put(`${ESTIMATE_URL}/estimate-service/estimates?ignoreSync=true`, estimate).then((res) => {
    res.data.data = mergeTillagePracticesInScenarios(res.data.data);
    return res.data;
  });

  if (estimate.sync && estimate.growerId) {
    QueueService.enqueue(sync);
  }
  return response;
};

const calculateAndSaveEstimate = async (estimate: IEstimate) => {
  const response = await API.post(`${ESTIMATE_URL}/estimate-service/estimates/calculate-and-save?ignoreSync=true`, estimate).then((res) => {
    res.data.data = mergeTillagePracticesInScenarios(res.data.data);
    return res.data;
  });

  if (estimate.sync && estimate.growerId) {
    QueueService.enqueue(() => syncEstimate(estimate));
  }
  return response;
};

const getBrazilSpecies = async () => {
  return API.get(`${ESTIMATE_URL}/estimate-service/calculator/brazil-species-v2`).then((res) => res.data);
};

const getBrazilCounties = (state: string) => {
  return API.get(`${ESTIMATE_URL}/estimate-service/calculator/brazil-counties/${state}`).then((res) => res.data);
};

const generatePracticesFromField = (practices, species: string[], inputs: string[], practiceType: PracticeTypes) => {
  const practiceShortCode = {
    coverCropping: 'CC',
    nitrogenReduction: 'NRR',
    legume: 'LA',
    reducedTillage: 'IT',
    noTillage: 'NT',
    seeding: 'RPBS',
    fertilization: 'RPF',
    grazing: 'RPIG',
    nitrogenOptimization: 'NO',
    ROW_CROP: {
      ICL: 'ICL2',
    },
    PASTURELAND: {
      ICL: 'ICL1',
    },
    coverPerennial: 'CP',
    coverAnnual: 'CA',
    inoculant: 'IN',
  };

  const practiceReadableCode = {
    coverCropping: 'Cover cropping',
    nitrogenReduction: 'Nitrogen reduction',
    legume: 'Legume Addition',
    reducedTillage: 'Reduced Tillage',
    noTillage: 'No Tillage',
    seeding: 'Biodiversity/Seeding',
    fertilization: 'Fertilization',
    grazing: 'Improved grazing',
    ICL: 'ICL',
    coverAnnual: 'Cover annual',
    coverPerennial: 'Cover perennial',
    inoculant: 'Inoculant',
  };

  const practiceDetails = {
    RPBS: {
      species: species[0],
      legume: 'Não',
    },
    RPF: {
      fertilizer: inputs.filter((input) => ['Sem insumos', 'Calcário', 'P'].indexOf(input) === -1)[0],
    },
    RPIG: {
      grazingIntensity: 'Remoção anual 25-75%',
    },
    ICL1: {
      eqCarbon: 0,
    },
    ICL2: {
      eqCarbon: 0,
    },
    IN: {
      eqCarbon: 0.53,
    },
    CA: {
      eqCarbon: 0,
    },
    CP: {
      eqCarbon: 0,
    },
  };

  for (const practice of practices) {
    practice.shortCode = practiceShortCode[practice.metadata.practiceName] || practiceShortCode[practiceType!][practice.metadata.practiceName];
    practice.readableCode = practiceReadableCode[practice.metadata.practiceName];
    practice.details = practiceDetails[practice.shortCode];
    practice.availablePaymentOptions = ['OPTION_A'];
  }

  return practices;
};

const downloadEstimateAsPdf = (
  template: string,
  data: IEstimateData,
  region: string,
  shouldConvertLead: boolean,
  option?: string,
  isChannelPartner?: boolean,
) => {
  return API.post(
    `${PDF_URL}/pdf/download?lang=${i18next.language}`,
    {
      templateName: template,
      data: data,
    },
    'blob',
  )
    .then((res) => {
      const file = new Blob([res.data], {
        type: 'application/pdf',
      });
      const fileURL = document.createElement('a');
      fileURL.href = URL.createObjectURL(file);

      const nameParts: string[] = []; // Estimate Name + First Name + Last Name + Report.pdf
      nameParts.push(data.estimate?.name || (region === 'BR' ? 'Nova_estimativa' : 'New_estimate'));
      if (data.producer?.name && data.producer.name !== 'Producer') {
        nameParts.push(data.producer.name);
      }

      if (region === 'BR') {
        nameParts.push(`Relatorio-${!option ? 'combinado' : option === 'optionA' ? 'precoFixo' : 'porcentagem'}.pdf`);
      } else {
        nameParts.push(`Report-${!option ? 'combined' : option === 'optionA' ? 'option-A' : 'option-B'}.pdf`);
      }
      fileURL.download = nameParts.join('_').replace(/ /g, '_');
      fileURL.click();
      URL.revokeObjectURL(fileURL.href);
      fileURL.remove();
      if (shouldConvertLead && isChannelPartner && !data.producer?.opportunityId && region !== 'BR') {
        return API.post(`${BASE_URL}/base/salesforce/convert-lead`, {
          leadId: data.producer?.leadId,
          accountFields: {
            name: `${data.producer?.company}`,
          },
          contactsFields: {
            firstName: data.producer?.firstName,
            lastName: data.producer?.lastName,
            email: data.producer?.email,
            phone: data.producer?.phone,
          },
          opportunityFields: {
            name: `${data.producer?.company} ${data.producer?.name}`,
            stageName: 'OA & Pitch Meeting',
          },
        });
      }
    })
    .catch(() => {
      AgoToast.showToast({
        title: 'Error while generating report.',
        message: 'Please try again.',
        type: 'error',
        toastId: 'REPORT_DOWNLOAD',
      });
    });
};

const getProducerDefaultName = (country: string) => {
  if (country === 'US') {
    return 'Producer';
  }
  return 'Produtor';
};

const parseEstimateData = (
  user: Record<string, any>,
  journey: Journey,
  producer: IProducer,
  calculatorResult: CalculatorResult,
  estimate: IEstimate,
): IEstimateData => {
  let growerName = journey ? journey.firstName + ' ' + journey.lastName : getProducerDefaultName(user.country);

  if (producer?.FirstName || producer?.Name) {
    const names: string[] = [];
    if (producer.FirstName) {
      names.push(producer.FirstName);
    }
    if (producer.LastName) {
      names.push(producer.LastName);
    }
    if (producer.Name && !names.length) {
      names.push(producer.Name);
    }
    growerName = names.join(' ') || '';
  }

  let estimateTransformed = cloneDeep(estimate);

  if (estimateTransformed.data[0].location.country === 'BR') {
    estimateTransformed = {
      ...estimateTransformed,
      data: estimateTransformed.data.map((sceanario) => {
        const pastureStatus = sceanario.baseline?.pastureStatus;
        const constantKey = pastureStatus ? `${pastureStatus}Pasture` : 'rowCrop';

        return {
          ...sceanario,
          practices: sceanario.practices.map((prac) => ({
            ...prac,
            details: {
              ...prac.details,
              eqCarbon: prac.brBufferConstants?.[constantKey]?.preBuffer ?? 0,
            },
          })),
        };
      }),
    };
  }

  return {
    producer: {
      name: growerName,
      firstName: producer?.FirstName,
      lastName: producer?.LastName,
      company: producer?.Company,
      email: producer?.Email,
      phone: producer?.Phone,
      leadId: producer?.leadId,
      opportunityId: producer?.opportunityId,
    },
    agoroUser: user,
    estimate: estimateTransformed,
    calculatorResult,
  };
};

const isEstimateDifferentFromField = (allFields, fieldsFromFS) => {
  const currentPracticesShortCodes = ['CA', 'CP', 'ICL1'];

  const fieldsMatched: string[] = [];
  for (const estimateField of allFields) {
    const found = fieldsFromFS.find((field) => {
      const practicesMatched: string[] = [];

      const filteredEstimateFieldPractices = estimateField.practices.filter((prac) => currentPracticesShortCodes.includes(prac.shortCode));

      const filteredFieldPractices = field.practices.filter((prac) => currentPracticesShortCodes.includes(prac.shortCode));

      for (const practice of filteredEstimateFieldPractices) {
        const found = filteredFieldPractices.find((fieldPractice) => {
          return fieldPractice.readableCode === practice.readableCode;
        });
        if (found) {
          practicesMatched.push(found.readableCode);
        } else {
          break;
        }
      }

      return (
        field.location.state === estimateField.location.state &&
        field.location.county === estimateField.location.county &&
        field.area === estimateField.area &&
        practicesMatched.length === filteredFieldPractices.length &&
        filteredFieldPractices.length === filteredEstimateFieldPractices.length &&
        fieldsMatched.indexOf(field.id) === -1
      );
    });

    if (found) {
      fieldsMatched.push(found.id);
    } else {
      break;
    }
  }

  return (
    fieldsFromFS.length > 0 && allFields.length > 0 && (fieldsFromFS.length !== fieldsMatched.length || fieldsFromFS.length !== allFields.length)
  );
};

const fetchPracticeCombinations = (version: string) => {
  return API.get(`${ESTIMATE_URL}/estimate-service/calculator/combinations/${version}`);
};

const getValidStates = (version: string) => {
  return API.get(`${ESTIMATE_URL}/estimate-service/calculator/states/${version}`);
};

export {
  generatePracticesFromField,
  getAvailablePractices,
  getStandaloneEstimate,
  getEstimateById,
  saveEstimate,
  calculateAndSaveEstimate,
  getBrazilSpecies,
  getBrazilCounties,
  parseEstimateData,
  downloadEstimateAsPdf,
  isEstimateDifferentFromField,
  getDraft,
  fetchPracticeCombinations,
  getValidStates,
};
